import styled from 'styled-components';
import { CheckmarkCircleOutlineIc } from '@dsch/dd-icons';

import { TextBoxWithUnit } from 'components/Toolkit/PublishFilters/TextBoxWithUnit/TextBoxWithUnit';
import { InputWithIcon } from 'components/Toolkit/Inputs/InputWithIcon';
import type { InputWithIconProps } from 'components/Toolkit/Inputs/InputWithIcon';
import { media } from 'helpers/breakpoints';
import {
  formatFormNumber,
  handleErrorMessage,
  removeNonNumericCharacters,
} from 'helpers/forms';
import { Field } from 'react-final-form';
import { MILEAGE_UNIT } from 'types';
import { FORM_VALUES } from 'components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.config';

export interface SimpleVehicleLookUpProps
  extends Omit<InputWithIconProps, 'icon' | 'handleClick'> {
  onVehicleLookUpInputCallback: (t: string) => void;
  buttonText: string;
  displayAttributes?: { [key: string]: string };
  disableVehicleRegLookupButton?: boolean;
}

const SInputWithIcon = styled(InputWithIcon)<{
  errorMessage?: string;
}>`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  input {
    text-transform: uppercase;

    ::placeholder {
      /* Recent browsers */
      text-transform: none;
    }
  }
`;

const VehicleValues = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing.M16};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const VehicleValueList = styled.ul`
  ${({ theme }) => theme.fontSize.M16}
  display: flex;
  flex-wrap: wrap;
`;

const VehicleValueListItem = styled.li`
  list-style: none;
  display: flex;
  align-items: center;

  &:before {
    width: ${({ theme }) => theme.spacing.S4};
    height: ${({ theme }) => theme.spacing.S4};
    background: ${({ theme }) => theme.colors.GREY_DARKER};
    margin-left: ${({ theme }) => theme.spacing.S8};
    margin-right: ${({ theme }) => theme.spacing.S8};
    content: '';
    border-radius: 50%;
  }

  &:first-child {
    &:before {
      width: ${({ theme }) => theme.spacing.S4};
      content: none;
      height: 100%;
    }
  }
`;

const SCheckmarkCircleOutlineIc = styled(CheckmarkCircleOutlineIc)`
  color: ${({ theme }) => theme.colors.GREEN};
  margin-right: ${({ theme }) => theme.spacing.M16};
`;

const SSvg = styled.svg`
  display: inline-block;
  position: absolute;
  height: 100%;
`;

const STextBoxWithUnit = styled(TextBoxWithUnit)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  width: 100%;

  ${media.large} {
    width: 344px;
  }
`;

function SimpleVehicleLookUp({
  className,
  errorMessage,
  onVehicleLookUpInputCallback,
  displayAttributes,
  buttonText,
  disableVehicleRegLookupButton,
}: SimpleVehicleLookUpProps) {
  function arrayBuilder(target: { [key: string]: string } | undefined) {
    if (target) {
      const arr = [];
      for (const property in target) {
        if (target[property].length > 0) {
          arr.push({
            displayName: `${property.charAt(0).toUpperCase()}${property
              .slice(1)
              .replace(/([A-Z]+)/g, ' $1')}`,
            value: target[property],
          });
        }
      }
      return arr;
    }
  }

  const ListValues = arrayBuilder(displayAttributes);

  return (
    <div className={className}>
      <Field name={FORM_VALUES.REGISTRATION_NO}>
        {({ input, meta }) => (
          <SInputWithIcon
            name={input.name}
            value={input.value}
            onChange={input.onChange}
            buttonText={buttonText}
            handleClick={onVehicleLookUpInputCallback}
            errorMessage={handleErrorMessage(meta) || errorMessage}
            label="Enter trade-in vehicle registration"
            placeholder="e.g. 09D123456"
            fullWidthIcon={false}
            isButtonDisabled={disableVehicleRegLookupButton}
            icon={
              <SSvg
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 38"
              >
                <path
                  d="M3 0a3 3 0 00-3 3v32a3 3 0 003 3h13V0H3z"
                  fill="#039"
                />
                <path
                  d="M8.13 6.74h.54l-.44.32.17.51-.44-.32-.44.32.16-.51-.44-.32h.55l.17-.51.17.5zM10.75 7.43h.55l-.45.32.17.51-.44-.31-.44.31.16-.5-.44-.33h.55l.17-.5.17.5zM12.66 9.33h.55l-.44.32.17.51-.45-.31-.44.31.17-.5-.44-.33h.54l.17-.51.17.51zM13.37 11.91h.55l-.45.32.17.51-.44-.31-.44.31.17-.5-.45-.33h.55l.17-.51.17.51zM12.67 14.5h.54l-.44.32.17.51-.44-.32-.45.32.17-.51-.44-.32h.55l.17-.51.17.5zM10.74 16.4h.55l-.44.31.17.52-.44-.32-.45.32.17-.52-.44-.32h.55l.17-.5.16.5zM8.12 17.08h.55l-.45.32.17.51-.44-.31-.44.31.17-.51-.45-.32h.55l.17-.51.17.51zM5.5 16.4h.55l-.44.31.17.52-.45-.32-.44.32.17-.52-.44-.31h.54l.17-.52.17.51zM3.58 14.5h.55l-.44.32.17.51-.44-.31-.45.31.17-.5-.44-.33h.55l.17-.5.16.5zM2.89 11.92h.54l-.44.31.17.52-.44-.32-.45.32.17-.52-.44-.31h.55l.17-.52.17.52zM3.58 9.33h.54l-.44.31.17.52-.44-.32-.45.32.17-.52-.44-.31h.55l.17-.52.17.52zM5.5 7.43h.55l-.44.32.17.51-.45-.31-.44.31.17-.51-.44-.32h.54l.17-.51.17.51z"
                  fill="#FFC200"
                />
              </SSvg>
            }
          />
        )}
      </Field>
      {ListValues ? (
        <>
          <VehicleValues>
            <SCheckmarkCircleOutlineIc />
            <VehicleValueList>
              {ListValues.map((listValue, index) => (
                <VehicleValueListItem key={index}>
                  {listValue.value}
                </VehicleValueListItem>
              ))}
            </VehicleValueList>
          </VehicleValues>
          <STextBoxWithUnit
            displayName="Enter vehicle mileage"
            placeholder="e.g. 80,000"
            valueName={FORM_VALUES.MILEAGE}
            unitName={FORM_VALUES.MILEAGE_UNIT}
            data-testid="mileage-input"
            helpMessage="Mileage is an important factor in a valuation"
            options={[
              { label: 'km', value: MILEAGE_UNIT.KILOMETRES },
              { label: 'miles', value: MILEAGE_UNIT.MILES },
            ]}
            flip
            optional
            isWideUnit
            willUseSubText
            formatFunction={formatFormNumber}
            parseFunction={removeNonNumericCharacters}
            maxLength={11}
          />
        </>
      ) : null}
    </div>
  );
}

export { SimpleVehicleLookUp };
